import React, { useState, useEffect, useCallback } from 'react';
import { FaTimes, FaSpinner, FaCalendarAlt, FaClock, FaChevronUp, FaChevronDown } from 'react-icons/fa';
import { appointmentService } from '../../services/appointmentService';
import { toast } from 'react-toastify';

const RescheduleModal = ({ isOpen, onClose, appointment, onSuccess }) => {
  const [selectedDate, setSelectedDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [isRescheduling, setIsRescheduling] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [showCalendar, setShowCalendar] = useState(false);
  const [bookedDates, setBookedDates] = useState([]);

  const getCollectionType = () => {
    if (!appointment?.appointmentTests || appointment.appointmentTests.length === 0) {
      return 'centre';
    }
    const firstTest = appointment.appointmentTests[0];
    const testCode = firstTest?.availableTest?.test_code || '';
    return testCode.startsWith('P') ? 'home' : 'centre';
  };

  const getTestIds = () => {
    if (!appointment?.appointmentTests || appointment.appointmentTests.length === 0) {
      return [];
    }
    return appointment.appointmentTests.map(test => test.availableTest?.id).filter(Boolean);
  };

  const getAppointmentDateString = () => {
    const appointmentDate = new Date(appointment.slot_start_time);
    const year = appointmentDate.getFullYear();
    const month = String(appointmentDate.getMonth() + 1).padStart(2, '0');
    const day = String(appointmentDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getTodayDateString = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isDateDisabled = (dateString) => {
    const appointmentDateString = getAppointmentDateString();
    const todayDateString = getTodayDateString();
    
    // Disable if date is before today or if it's the appointment's current date
    return dateString < todayDateString || dateString === appointmentDateString;
  };


  const handleReschedule = async () => {
    if (!selectedDate || !selectedSlot) {
      toast.warning('Please select both date and time slot');
      return;
    }

    setIsRescheduling(true);
    try {
      await appointmentService.rescheduleAppointment(appointment.id, selectedDate, selectedSlot);
      toast.success('Appointment rescheduled successfully');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error rescheduling appointment:', error);
      toast.error('Failed to reschedule appointment');
    } finally {
      setIsRescheduling(false);
    }
  };

  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return '';
    const [year, month, day] = dateString.split('-');
    return `${day}-${month}-${year}`;
  };

  const handleCalendarDateClick = async (day) => {
    const newDate = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day);
    const dateString = formatDateForAPI(newDate);
    setSelectedDate(dateString);
    setSelectedSlot(null);
    setSlots([]);
    setShowCalendar(false);

    setIsLoadingSlots(true);
    try {
      const collectionType = getCollectionType();
      const testIds = getTestIds();
      const facilityId = appointment?.facility?.id || 303;
      const addressId = appointment?.address?.id || 15;

      const slotsData = await appointmentService.getSlots(
        dateString,
        collectionType,
        testIds,
        facilityId,
        addressId
      );

      setSlots(slotsData);
      if (slotsData.length === 0) {
        toast.info('No slots available for the selected date');
      }
    } catch (error) {
      console.error('Error fetching slots:', error);
      toast.error('Failed to fetch available slots');
      setSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handlePrevMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1));
  };

  const handleToday = () => {
    const today = new Date();
    setCalendarMonth(today);
    handleCalendarDateClick(today.getDate());
  };

  const handleClearDate = () => {
    setSelectedDate('');
    setSlots([]);
    setSelectedSlot(null);
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(calendarMonth);
    const firstDay = getFirstDayOfMonth(calendarMonth);
    const days = [];
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

    for (let i = 0; i < firstDay; i++) {
      days.push(null);
    }

    for (let i = 1; i <= daysInMonth; i++) {
      days.push(i);
    }

    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) {
      days.push(null);
    }

    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-4 w-80">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">
            {monthNames[calendarMonth.getMonth()]}, {calendarMonth.getFullYear()}
          </h3>
          <div className="flex gap-2">
            <button onClick={handlePrevMonth} className="p-1 hover:bg-gray-100 rounded transition-colors">
              <FaChevronUp size={14} />
            </button>
            <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded transition-colors">
              <FaChevronDown size={14} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-7 gap-1 mb-2">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-xs font-semibold text-gray-600 py-1">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-1">
          {days.map((day, idx) => {
            if (day === null) {
              return <div key={`empty-${idx}`} className="aspect-square"></div>;
            }

            const dateString = formatDateForAPI(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth(), day));
            const disabled = isDateDisabled(dateString);
            const selected = selectedDate === dateString;

            return (
              <button
                key={day}
                onClick={() => !disabled && handleCalendarDateClick(day)}
                disabled={disabled}
                className={`aspect-square text-sm font-medium rounded transition-colors flex items-center justify-center ${
                  selected ? 'bg-orange-500 text-white' : disabled ? 'text-gray-300 cursor-not-allowed' : 'text-gray-700 hover:bg-orange-100'
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-200">
          <button onClick={handleClearDate} className="flex-1 text-sm text-orange-500 hover:text-orange-600 font-medium transition-colors">
            Clear
          </button>
          <button onClick={handleToday} className="flex-1 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors">
            Today
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Reschedule Appointment</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Appointment Info */}
          <div className="bg-gray-50 p-3 rounded-lg text-sm">
            <p className="text-gray-600">
              <span className="font-medium text-gray-900">{appointment?.profile?.name}</span>
            </p>
            <p className="text-gray-600 text-xs mt-1">
              Current: {appointment?.slot_start_time ? new Date(appointment.slot_start_time).toLocaleDateString() : 'N/A'}
            </p>
          </div>

          {/* Date Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <FaCalendarAlt className="inline mr-2" size={14} />
              Select New Date
            </label>
            <div className="relative">
              <button
                onClick={() => setShowCalendar(!showCalendar)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-left bg-white hover:bg-gray-50 transition-colors flex items-center justify-between"
              >
                <span className={selectedDate ? 'text-gray-900 font-medium' : 'text-gray-500'}>
                  {selectedDate ? formatDateForDisplay(selectedDate) : 'Select a date'}
                </span>
                <FaCalendarAlt size={16} className="text-gray-400" />
              </button>
              {showCalendar && (
                <div className="absolute top-full left-0 mt-2 z-20">
                  {renderCalendar()}
                </div>
              )}
            </div>
          </div>

          {/* Slots Selection */}
          {selectedDate && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <FaClock className="inline mr-2" size={14} />
                Select Time Slot
              </label>
              {isLoadingSlots ? (
                <div className="flex items-center justify-center py-4">
                  <FaSpinner className="animate-spin text-orange-500" size={20} />
                  <span className="ml-2 text-sm text-gray-600">Loading slots...</span>
                </div>
              ) : slots.length > 0 ? (
                <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {slots.map((slot) => (
                    <button
                      key={slot.slot_id}
                      onClick={() => setSelectedSlot(slot)}
                      className={`p-2 rounded-lg border-2 transition-colors text-xs font-medium ${
                        selectedSlot?.slot_id === slot.slot_id
                          ? 'border-orange-500 bg-orange-50 text-orange-700'
                          : 'border-gray-300 bg-white text-gray-700 hover:border-orange-300'
                      }`}
                    >
                      <div>{slot.timeslots}</div>
                      <div className="text-xs opacity-75 capitalize">{slot.slot_type}</div>
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-sm text-gray-500">
                  No slots available for this date
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2 p-4 border-t border-gray-200">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleReschedule}
            disabled={!selectedDate || !selectedSlot || isRescheduling}
            className="flex-1 px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isRescheduling && <FaSpinner className="animate-spin" size={16} />}
            {isRescheduling ? 'Rescheduling...' : 'Reschedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RescheduleModal;
